/**
 * Indebolimento intenzionale dell'AI per i livelli bassi.
 *
 * La difficoltà è sempre stata SOLO la profondità del minimax, e non bastava: la
 * quiescence search gira a ogni livello e risolve fino a 4 semi-mosse di catture,
 * quindi anche "Molto Facile" (depth 2) vedeva ogni combinazione di presa e non
 * lasciava mai un pezzo in presa a una tattica corta. Misurato in self-play contro
 * un principiante simulato (mossa a caso fra quelle che non regalano un pezzo):
 * vinceva 18-1 con undici pedine di margine.
 *
 * Qui la difficoltà diventa profondità + una quota di mosse davvero casuali. Le
 * catture restano obbligatorie anche nel ramo casuale — `getAllLegalMoves`
 * restituisce solo catture quando esistono — quindi l'AI non gioca mai una mossa
 * che sembri illegale, solo mosse che un principiante potrebbe fare.
 *
 * ── Due strade provate e scartate, per non ritentarle ──────────────────────────
 *
 * 1. SPEGNERE LA QUIESCENCE ai livelli bassi. Non indebolisce l'AI: la ribalta.
 *    Depth 1 senza quiescence PERDE 0-16 contro un giocatore puramente casuale
 *    (-14 pedine). Il motivo è un difetto latente della valutazione:
 *    `evaluatePosition` somma MOBILITY_BONUS * numero di mosse legali per il lato
 *    al tratto, ma nella dama la presa è OBBLIGATORIA, quindi regalare un pezzo
 *    azzera le mosse dell'avversario e la valutazione statica PREMIA la mossa che
 *    regala il pezzo. Su `W:W31,32,33,37,38:B17,18,21,22` le tre mosse che
 *    concedono una presa doppia sono le tre col punteggio più alto (97/97/94
 *    contro 88/85/73). Oggi la quiescence è l'unica cosa che maschera il difetto,
 *    a ogni livello. Togliere anche il termine di mobilità non basta (5-11 contro
 *    il casuale): a depth 1 le mosse tranquille hanno tutte lo stesso materiale e
 *    la scelta finisce guidata da avanzamento/centro, che spinge le pedine in presa.
 *
 * 2. L'"ERRORE REALISTICO" DEI BOT ONLINE (2ª/3ª mossa migliore, pesi 1/(i+1),
 *    tetto al peggioramento — `_pickRealisticBlunder` in bot.instance.ts). A bassa
 *    profondità non indebolisce abbastanza: depth 2 con il 20% di errori batte
 *    ancora il principiante 16-0. I pesi concentrano la scelta sulla 2ª migliore,
 *    che a depth 2 vale quasi quanto la migliore. Resta ai bot online, dove serve a
 *    sembrare umani e non a perdere.
 */

import type { BoardState, Move } from '@draughts/shared';
import { getAllLegalMoves } from '../moves/capture-moves.js';
import { findBestMove } from './minimax.js';

/** I sei livelli offerti dal selettore di difficoltà, dal più debole al più forte. */
export type AIStrengthLevel = 'very-easy' | 'easy' | 'medium' | 'hard' | 'expert' | 'master';

/** Come gioca un livello: quanto guarda avanti e quanto spesso tira a caso. */
export interface AIStrengthProfile {
  /**
   * Profondità del minimax. Sempre >= 1: a 0 la ricerca non restituisce alcuna
   * mossa e il chiamante resterebbe in attesa per sempre.
   */
  readonly depth: number;
  /**
   * Probabilità [0,1] di giocare una mossa legale QUALSIASI invece di cercare.
   * È l'unica leva con un'escursione reale ai livelli bassi (vedi in testa al file).
   */
  readonly randomMoveRate: number;
}

/**
 * Tarati in self-play su International e Italian 8x8, colori alternati, contro due
 * avversari simulati: il principiante "attento" (mai una mossa che regala un pezzo)
 * e quello "sbadato" (lo stesso, ma 35% delle volte tira a caso). Un principiante
 * vero sta fra i due.
 *
 *   livello        vs casuale   vs sbadato   vs attento
 *   very-easy         18-2         2-14         2-18
 *   easy              16-0        15-1          6-9
 *   (prima)  d2       16-0        16-0         18-1   <- il problema
 *
 * `very-easy` perde contro entrambi i principianti — è il punto — ma contro un
 * giocatore puramente casuale vince ancora, quindi non si autodistrugge. Alzando
 * `randomMoveRate` a 0.6-0.7 perde 0-20 anche contro l'attento: troppo.
 * `easy` sta alla pari con l'attento e schiaccia lo sbadato: resta un avversario
 * vero, un gradino sopra (nello scontro diretto batte `very-easy` 15-1).
 *
 * Dal medium in su niente casualità: quei livelli non erano il problema.
 */
export const AI_STRENGTH_PROFILES: Readonly<Record<AIStrengthLevel, AIStrengthProfile>> = {
  'very-easy': { depth: 1, randomMoveRate: 0.5 },
  easy: { depth: 2, randomMoveRate: 0.3 },
  medium: { depth: 6, randomMoveRate: 0 },
  hard: { depth: 7, randomMoveRate: 0 },
  expert: { depth: 8, randomMoveRate: 0 },
  master: { depth: 10, randomMoveRate: 0 },
};

/**
 * Come `findBestMove`, ma con l'indebolimento del profilo.
 *
 * La mossa restituita proviene sempre TESTUALMENTE da `getAllLegalMoves` (array
 * `captures` intatto): il client rivalida il risultato dell'AI con lo stesso
 * confronto su from/to e case catturate, e scarterebbe in silenzio una mossa
 * ricostruita a mano.
 *
 * `rng` è iniettabile SOLO per i test (stesso schema di `drawAndApplyOpening`).
 * Non attraversa mai il confine del Web Worker: le funzioni non sono clonabili.
 */
export function findBestMoveWeakened(
  board: BoardState,
  profile: AIStrengthProfile,
  rng: () => number = Math.random,
): Move | null {
  const moves = getAllLegalMoves(board);
  if (moves.length === 0) return null;
  // Mossa forzata (di norma una cattura obbligata): non c'è niente da indebolire,
  // e si evita di far girare la ricerca. Stessa scorciatoia di `searchBest`.
  if (moves.length === 1) return moves[0]!;

  if (profile.randomMoveRate > 0 && rng() < profile.randomMoveRate) {
    return moves[Math.floor(rng() * moves.length)]!;
  }

  return findBestMove(board, Math.max(1, profile.depth));
}
